import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { faCheck, faCircleCheck, faCircleExclamation, faXmark } from '@fortawesome/pro-regular-svg-icons';

import StringField from '../../core/components/Fields/StringField.tsx';
import FormField from '../../core/components/FormField.tsx';
import Table, { RowControlIconButton } from '../../core/components/Table.tsx';
import ConfirmModal from '../../core/components/ConfirmModal.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import classnames from '../../core/utils/classnames.tsx';
import ClientLookupInput from '../ClientLookupInput.tsx';

import { FormClientRow } from './FormsConfig.tsx';
import { CONFIRM_SELF_SERVICE_FORM_CLIENT, REJECT_SELF_SERVICE_FORM_CLIENT } from './queries.ts';

const ConfirmIcon = withIcon(faCheck);
const RejectIcon = withIcon(faXmark);
const EmailMatchedIcon = withIcon(faCircleCheck);
const DomainMatchedIcon = withIcon(faCircleExclamation);

interface ConfirmFormData {
    clientCustomerNumber: string;
    clientName: string;
}

interface FormRequestsSectionProps {
    requests: FormClientRow[];
    clientLookupDefinitionId: string | null;
    loading: boolean;
    onChanged: () => Promise<void>;
}

const FormRequestsSection = ({ requests, clientLookupDefinitionId, loading, onChanged }: FormRequestsSectionProps) => {
    const { t } = useTranslation('forms');
    const { publishToast } = useToaster();

    const [confirmClient] = useMutation(CONFIRM_SELF_SERVICE_FORM_CLIENT);
    const [rejectClient] = useMutation(REJECT_SELF_SERVICE_FORM_CLIENT);

    const [pendingConfirm, setPendingConfirm] = useState<FormClientRow | null>(null);
    const [pendingReject, setPendingReject] = useState<FormClientRow | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const selectedClientDataRef = useRef<Record<string, unknown> | null>(null);

    const form = useForm<ConfirmFormData>({
        defaultValues: { clientCustomerNumber: '', clientName: '' },
        mode: 'onTouched',
    });
    const formErrors = form.formState.errors;

    useEffect(() => {
        if (pendingConfirm) {
            form.reset({
                clientCustomerNumber: pendingConfirm.clientCustomerNumber,
                clientName: pendingConfirm.clientName,
            });
            selectedClientDataRef.current = null;
        }
    }, [pendingConfirm]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleConfirm = async (formData: ConfirmFormData) => {
        if (!pendingConfirm) return;
        setSubmitting(true);
        try {
            await confirmClient({
                variables: {
                    clientId: pendingConfirm.id,
                    clientCustomerNumber: formData.clientCustomerNumber.trim(),
                    clientName: formData.clientName.trim(),
                    clientData: selectedClientDataRef.current ?? undefined,
                },
            });
            await onChanged();
            publishToast({ description: t('tenant.requests.toast.confirmSuccess'), status: 'success' });
            setPendingConfirm(null);
        } catch (e) {
            console.error(e);
            publishToast({ description: t('tenant.requests.toast.confirmError'), status: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = async () => {
        if (!pendingReject) return;
        setSubmitting(true);
        try {
            await rejectClient({ variables: { clientId: pendingReject.id } });
            await onChanged();
            publishToast({ description: t('tenant.requests.toast.rejectSuccess'), status: 'success' });
            setPendingReject(null);
        } catch (e) {
            console.error(e);
            publishToast({ description: t('tenant.requests.toast.rejectError'), status: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    if (requests.length === 0) return null;

    return (
        <>
            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                    <h3 className="text-base font-semibold text-content-strong">{t('tenant.requests.title')}</h3>
                    <p className="text-sm text-content-medium">{t('tenant.requests.description')}</p>
                </div>
                <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                    <Table.Head>
                        <Table.Row>
                            <Table.HeadCell>{t('tenant.requests.table.requester')}</Table.HeadCell>
                            <Table.HeadCell>{t('tenant.requests.table.customer')}</Table.HeadCell>
                            <Table.HeadCell>{t('tenant.requests.table.form')}</Table.HeadCell>
                            <Table.HeadCell>{t('tenant.requests.table.note')}</Table.HeadCell>
                            <Table.HeadCell className="min-w-32 w-32">
                                {t('tenant.requests.table.createdAt')}
                            </Table.HeadCell>
                            <Table.HeadCell sticky="right" className="min-w-28 w-28" />
                        </Table.Row>
                    </Table.Head>
                    <Table.Body>
                        {requests.map((row) => (
                            <Table.Row key={row.id}>
                                <Table.Cell>
                                    <div className="flex flex-col min-w-0">
                                        <div className="flex items-center gap-1.5 min-w-0">
                                            <span className="text-content-strong truncate">{row.requesterEmail}</span>
                                            {row.requesterMatchType === 'email' && (
                                                <EmailMatchedIcon
                                                    className="text-content-success-strong shrink-0"
                                                    title={t('tenant.requests.matchedByEmail')}
                                                />
                                            )}
                                            {row.requesterMatchType === 'domain' && (
                                                <DomainMatchedIcon
                                                    className="text-content-warning-strong shrink-0"
                                                    title={t('tenant.requests.matchedByDomain')}
                                                />
                                            )}
                                        </div>
                                        {row.requesterPhone && (
                                            <span className="text-xs text-content-medium truncate">
                                                {row.requesterPhone}
                                            </span>
                                        )}
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    {row.clientCustomerNumber ? (
                                        <div className="flex flex-col min-w-0">
                                            <span className="text-content-strong truncate">
                                                {row.clientName || row.clientCustomerNumber}
                                            </span>
                                            {row.clientName && (
                                                <span className="text-xs text-content-medium truncate">
                                                    {row.clientCustomerNumber}
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-content-weak">—</span>
                                    )}
                                </Table.Cell>
                                <Table.Cell className="text-content-medium">
                                    {row.form.name || t('tenant.forms.unnamed')}
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-content-medium line-clamp-2">{row.note}</span>
                                </Table.Cell>
                                <Table.Cell className="whitespace-nowrap text-content-medium">
                                    {format(new Date(row.createdAt), 'PP')}
                                </Table.Cell>
                                <Table.Cell sticky="right" className="w-28">
                                    <div className="flex gap-1 justify-end">
                                        <RowControlIconButton
                                            className="hover:bg-surface-success-weak text-content-success-strong hover:text-content-success-strong"
                                            onClick={() => setPendingConfirm(row)}
                                            title={t('tenant.requests.confirm')}
                                        >
                                            <ConfirmIcon />
                                        </RowControlIconButton>
                                        <RowControlIconButton
                                            className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                                            onClick={() => setPendingReject(row)}
                                            title={t('tenant.requests.reject')}
                                        >
                                            <RejectIcon />
                                        </RowControlIconButton>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </div>

            {pendingConfirm && (
                <ConfirmModal
                    visible
                    title={t('tenant.requests.confirmModal.title')}
                    onConfirm={() => form.handleSubmit(handleConfirm)()}
                    onCancel={() => !submitting && setPendingConfirm(null)}
                    labelConfirm={t('tenant.requests.confirmModal.submit')}
                    confirmDisabled={submitting}
                >
                    <div className="flex flex-col gap-4">
                        <p className="text-sm text-content-medium">
                            {t('tenant.requests.confirmModal.body', { email: pendingConfirm.requesterEmail })}
                        </p>
                        {pendingConfirm.note && (
                            <p className="text-sm text-content-medium border-l-2 border-solid border-line-weak pl-2">
                                {pendingConfirm.note}
                            </p>
                        )}

                        <Controller
                            name="clientCustomerNumber"
                            control={form.control}
                            rules={{ required: t('tenant.errors.missingCustomerNumber') }}
                            render={({ field }) => (
                                <FormField
                                    label={t('tenant.shared.customer')}
                                    required
                                    error={formErrors.clientCustomerNumber}
                                    helpText={
                                        clientLookupDefinitionId
                                            ? t('tenant.shared.customerHelp')
                                            : t('tenant.shared.customerHelpNoMasterData')
                                    }
                                >
                                    <ClientLookupInput
                                        clientLookupDefinitionId={clientLookupDefinitionId}
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        onClientSelect={({ customerNumber, companyName, entity }) => {
                                            selectedClientDataRef.current = entity;
                                            field.onChange(customerNumber);
                                            form.setValue('clientName', companyName);
                                        }}
                                    />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="clientName"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('tenant.shared.customerName')}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />
                    </div>
                </ConfirmModal>
            )}

            {pendingReject && (
                <ConfirmModal
                    visible
                    title={t('tenant.requests.rejectModal.title')}
                    onConfirm={handleReject}
                    onCancel={() => !submitting && setPendingReject(null)}
                    labelConfirm={t('tenant.requests.rejectModal.confirm')}
                    confirmDisabled={submitting}
                    danger
                >
                    <p className="text-sm text-content-medium">
                        {t('tenant.requests.rejectModal.body', { email: pendingReject.requesterEmail })}
                    </p>
                </ConfirmModal>
            )}
        </>
    );
};

export default FormRequestsSection;
